import type { ShaderSource } from "./quad-shader";

/**
 * Customized outline compositing shader. The dual-language structure and
 * PlayCanvas WGSL declarations are adapted from the donor's
 * src/outline/shaders/outline.ts at revision
 * 99958e178f0271cd7a8d82416b6c9c175140b035 (ISC-licensed).
 *
 * The algorithm, bindings, and background-tint behavior remain owned by the
 * configurator's original effect.
 */
export function createOutlineShader(kernel: readonly string[]): ShaderSource {
	const kernelAssignments = kernel
		.map((uv, index) => `kKernel[${index}] = vec2(${uv});`)
		.join("\n\t\t\t\t\t");

	return {
		glsl: `
			#define half lowp float
			#define half2 lowp vec2
			#define half4 lowp vec4

			uniform float uHalfThickness;
			uniform vec4 uInvTextureSize;
			uniform vec4 uOutlineCol;
			uniform vec4 uInteriorCol;
			uniform vec4 uBackgroundCol;
			uniform sampler2D uColorBuffer;
			uniform sampler2D uOutlineTex;

			varying vec2 uv0;

			void main(void) {
				half2 kKernel[${kernel.length}];
				${kernelAssignments}

				half maxAlpha = 0.0;
				half minAlpha = 1.0;

				for (int si = 0; si < ${kernel.length}; si++) {
					half2 duv = kKernel[si] * uInvTextureSize.xy * uHalfThickness;
					half alpha = texture2D(uOutlineTex, uv0 + duv).a;
					maxAlpha = max(maxAlpha, alpha);
					minAlpha = min(minAlpha, alpha);
				}

				half interiorFactor = ceil(minAlpha);
				half outlineFactor = (1.0 - interiorFactor) * ceil(maxAlpha);
				half outlineOrInteriorFactor = max(interiorFactor, outlineFactor);
				half backgroundFactor = 1.0 - outlineOrInteriorFactor;

				half4 color = mix(uOutlineCol, uInteriorCol, interiorFactor);
				half4 sceneColor = texture2D(uColorBuffer, uv0);
				half4 background = mix(sceneColor, uBackgroundCol,
					backgroundFactor * uBackgroundCol.a);
				gl_FragColor = mix(background, color,
					outlineOrInteriorFactor * color.a);
			}
		`,
		wgsl: `
			uniform uHalfThickness: f32;
			uniform uInvTextureSize: vec4f;
			uniform uOutlineCol: vec4f;
			uniform uInteriorCol: vec4f;
			uniform uBackgroundCol: vec4f;
			var uColorBuffer: texture_2d<f32>;
			var uColorBufferSampler: sampler;
			var uOutlineTex: texture_2d<f32>;
			var uOutlineTexSampler: sampler;

			varying uv0: vec2f;

			@fragment fn fragmentMain(input: FragmentInput) -> FragmentOutput {
				var output: FragmentOutput;
				var kKernel: array<vec2f, ${kernel.length}>;
				${kernel
					.map((uv, index) => `kKernel[${index}] = vec2f(${uv});`)
					.join("\n\t\t\t\t")}

				var maxAlpha = 0.0;
				var minAlpha = 1.0;

				for (var si = 0; si < ${kernel.length}; si++) {
					let duv = kKernel[si] * uniform.uInvTextureSize.xy * uniform.uHalfThickness;
					let alpha = textureSample(uOutlineTex, uOutlineTexSampler, uv0 + duv).a;
					maxAlpha = max(maxAlpha, alpha);
					minAlpha = min(minAlpha, alpha);
				}

				let interiorFactor = ceil(minAlpha);
				let outlineFactor = (1.0 - interiorFactor) * ceil(maxAlpha);
				let outlineOrInteriorFactor = max(interiorFactor, outlineFactor);
				let backgroundFactor = 1.0 - outlineOrInteriorFactor;

				let color = mix(uniform.uOutlineCol, uniform.uInteriorCol, interiorFactor);
				let sceneColor = textureSample(uColorBuffer, uColorBufferSampler, uv0);
				let background = mix(sceneColor, uniform.uBackgroundCol,
					backgroundFactor * uniform.uBackgroundCol.a);
				output.color = mix(background, color,
					outlineOrInteriorFactor * color.a);
				return output;
			}
		`,
	};
}
